/**
 * RepurposingOpportunitiesStrip
 *
 * Cross-portfolio carousel of older top-performing posts that haven't been
 * remixed yet - surfacing them as "candidates for a second life."
 *
 * Popover design: Dashboard-matched pattern.
 * Coral gradient header · full-width thumbnail · metric grid · coral summary bar · caption · CTAs.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { lifecycleStageLabel } from '@/utils/portfolioLabels';
import { useTimezone } from '@/contexts/TimezoneContext';
import { ICONS } from '@/constants/icons';
import { SelectChevron } from '@/components/ui/Select';
import { Icon } from '@/components/ui/Icon';
import { HoverCard } from '@/components/ui/HoverCard';
import { HorizontalCardCarousel } from '@/components/dashboard/HorizontalCardCarousel';
import { DashboardPostPopover } from '@/components/dashboard/DashboardPostPopover';
import { GlossaryTooltip } from '@/components/portfolio/GlossaryTooltip';
import { PlatformAvatars } from './PlatformAvatars';
import { type RepurposeOpportunity } from '@/data/repurposeOpportunities.types';
import { usePortfolioHighlights } from '@/hooks/usePortfolio';
import type { PortfolioRepurposeCandidate } from '@/services/portfolio.service';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as agencyService from '@/services/agency.service';
import { setAccessToken } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';

// ── Constants ─────────────────────────────────────────────────────────────────

const STRIP_STORAGE_KEY = 'portfolio-repurpose-strip-open';

// ── Lifecycle badge config ────────────────────────────────────────────────────

const LIFECYCLE_CONFIG: Record<string, { pill: string; label: string }> = {
  active:     { pill: 'bg-primary-100 text-primary-700',   label: 'Active' },
  onboarding: { pill: 'bg-peach/60 text-coral-700',     label: 'Onboarding' },
  churning:   { pill: 'bg-coral-100 text-coral-700',       label: 'Churning' },
  paused:     { pill: 'bg-gray-100 text-gray-600',         label: 'Paused' },
};

// ── Metric parsing ────────────────────────────────────────────────────────────

/**
 * Parse metric strings like "12k impressions", "ER 5.8%", "4.8k saves"
 * into { value, unit } for display.
 *
 * Rules:
 * - "ER 5.8%"       → value="5.8%", unit="ER"
 * - "12k impressions" → value="12k", unit="IMPRESSIONS"
 * - "4.8k saves"    → value="4.8k", unit="SAVES"
 */
/** Format a raw number into a compact human-readable string: 12345 → "12.3k". */
function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function parseMetric(metricStr: string): { value: string; unit: string } {
  const str = metricStr.trim();

  // Handle "ER 4.2%" format - unit prefix
  const erMatch = str.match(/^(ER)\s+(.+)$/i);
  if (erMatch) {
    return { value: erMatch[2], unit: 'ER' };
  }

  // Split on first space: first token = value, rest = unit
  const spaceIdx = str.indexOf(' ');
  if (spaceIdx === -1) {
    return { value: str, unit: '' };
  }

  const value = str.slice(0, spaceIdx);
  const unit = str.slice(spaceIdx + 1).toUpperCase();
  return { value, unit };
}

// ── Toast ─────────────────────────────────────────────────────────────────────

interface ToastState { visible: boolean; message: string }

// ── Repurpose Popover - Dashboard-matched pattern ────────────────────────────

interface RepurposePopoverProps {
  opportunity: RepurposeOpportunity;
  formatRelativeDate: (date: string) => string;
  onStartRemix: () => void;
  onSeeOriginal: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const RepurposePopover = ({
  opportunity,
  formatRelativeDate,
  onStartRemix,
  onSeeOriginal,
  t,
}: RepurposePopoverProps) => {
  // Backend always provides lifecycleStage; default to 'active' if missing.
  const lifecycle = LIFECYCLE_CONFIG[opportunity.lifecycleStage ?? 'active'] ?? LIFECYCLE_CONFIG.active;
  const { value: perfValue, unit: perfUnit } = parseMetric(opportunity.originalMetric);

  return (
    <DashboardPostPopover
      className="w-[360px] lg:w-[380px] max-h-[min(80vh,640px)] flex flex-col"
      gradient="from-secondary-500 to-secondary-700"
      headerTone="secondary"
      headerIcon={ICONS.REFRESH}
      headerLabel={t('repurpose_popover_title', { defaultValue: 'Repurposing opportunity' })}
      headerLines={[
        { icon: ICONS.CLOCK, text: formatRelativeDate(opportunity.publishedAt) },
      ]}
      postMedia={opportunity.media ?? []}
      caption={opportunity.caption}
      // Real media → CarouselThumb (handles IMAGE/VIDEO/PDF); empty → branded gradient
      thumbnail={
        (opportunity.media ?? []).length === 0 ? (
          <div
            className="aspect-square rounded-lg overflow-hidden relative"
            style={{ background: opportunity.thumbnailPlaceholderGradient }}
            aria-hidden="true"
          >
            <div className="absolute top-2 left-2">
              <PlatformAvatars platforms={opportunity.platforms} channelNames={opportunity.channelNames} profilePhotoUrls={opportunity.channelProfilePhotoUrls} size={20} />
            </div>
            <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/35 backdrop-blur-sm">
              <span className="text-[11px] font-semibold text-white leading-none">
                {perfValue}{perfUnit ? ` ${perfUnit}` : ''}
              </span>
            </div>
          </div>
        ) : undefined
      }
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:border-primary-600"
            onClick={onSeeOriginal}
            aria-label={t('repurpose_cta_see_original', { defaultValue: 'See original' })}
          >
            <i className="hm hm-link" style={{ fontSize: 12 }} aria-hidden="true" />
            {t('repurpose_cta_see_original', { defaultValue: 'See original' })}
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-secondary-500 text-secondary-contrast text-xs font-semibold hover:bg-secondary-600 transition-colors border border-transparent focus:outline-none focus-visible:border-secondary-600"
            onClick={onStartRemix}
            aria-label={t('repurpose_cta_start_remix', { defaultValue: 'Start remix' })}
          >
            <i className="hm hm-refresh" style={{ fontSize: 12 }} aria-hidden="true" />
            {t('repurpose_cta_start_remix', { defaultValue: 'Start remix' })}
          </button>
        </div>
      }
    >
      {/* Real analytics for the winning version of this post */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-2">
          {opportunity.totalEngagements > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('repurpose_engagements_label', { defaultValue: 'Engagements' })}
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatCompact(opportunity.totalEngagements)}
              </div>
            </div>
          )}
          {opportunity.totalReach > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('repurpose_reach_label', { defaultValue: 'Reach' })}
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatCompact(opportunity.totalReach)}
              </div>
            </div>
          )}
          {opportunity.totalImpressions > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('repurpose_impressions_label', { defaultValue: 'Impressions' })}
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatCompact(opportunity.totalImpressions)}
              </div>
            </div>
          )}
          {opportunity.engagementRate !== undefined && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('repurpose_er_label', { defaultValue: 'Engagement Rate' })}
              </div>
              <div className="text-sm font-bold text-gray-900">{opportunity.engagementRate}%</div>
            </div>
          )}
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 mb-0.5">
              {t('repurpose_days_ago_label', { defaultValue: 'Days ago' })}
            </div>
            <div className="text-sm font-bold text-gray-900">{opportunity.daysAgo}</div>
          </div>
        </div>
      </div>
      {/* Client row */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          {t('award_client_label', { defaultValue: 'Client' })}
        </p>
        <div className="flex items-center gap-2">
          {opportunity.clientLogoUrl ? (
            <img src={opportunity.clientLogoUrl} alt="" className="w-6 h-6 rounded-full ring-2 ring-white/40 object-cover shrink-0" />
          ) : (
            <div
              className={cn(
                'w-6 h-6 rounded-md flex items-center justify-center font-bold text-white shrink-0 text-[11px]',
                opportunity.clientAvatarColor
              )}
              aria-hidden
            >
              {opportunity.clientName.charAt(0)}
            </div>
          )}
          <span className="text-sm font-semibold text-gray-900 flex-1 truncate">{opportunity.clientName}</span>
          <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0', lifecycle.pill)}>
            {lifecycleStageLabel(t, opportunity.lifecycleStage ?? 'active')}
          </span>
        </div>
      </div>
    </DashboardPostPopover>
  );
};

// ── Opportunity Card (160px - matches Dashboard pattern exactly) ──────────────

interface OpportunityCardProps {
  opportunity: RepurposeOpportunity;
  onStartRemix: (opportunity: RepurposeOpportunity) => void;
  onSeeOriginal: (opportunity: RepurposeOpportunity) => void;
  formatRelativeDate: (date: string) => string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const OpportunityCard = ({
  opportunity,
  onStartRemix,
  onSeeOriginal,
  formatRelativeDate,
  t,
}: OpportunityCardProps) => {
  const handleStartRemix = useCallback(
    () => onStartRemix(opportunity),
    [opportunity, onStartRemix]
  );
  const handleSeeOriginal = useCallback(
    () => onSeeOriginal(opportunity),
    [opportunity, onSeeOriginal]
  );

  // 160×160 card trigger - days-ago is the hero signal in the bottom overlay
  const cardThumb = opportunity.media?.[0]?.thumbnailUrl ?? opportunity.media?.[0]?.originalUrl ?? null;
  const cardTrigger = (
    <div
      className="flex-none w-[160px] aspect-square rounded-xl overflow-hidden relative cursor-pointer hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
      style={cardThumb ? undefined : { background: opportunity.thumbnailPlaceholderGradient }}
      aria-label={`${opportunity.clientName}: ${opportunity.originalMetric} - ${opportunity.reason}`}
    >
      {cardThumb && (
        <img
          src={cardThumb}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* Top overlay - stacked platform avatars (one circle per platform) */}
      <div className="absolute top-0 inset-x-0 p-2 flex items-center gap-1">
        <PlatformAvatars platforms={opportunity.platforms} channelNames={opportunity.channelNames} profilePhotoUrls={opportunity.channelProfilePhotoUrls} size={20} />
        {/* Client logo or initial - top-right */}
        <div className="ml-auto">
          {opportunity.clientLogoUrl ? (
            <img src={opportunity.clientLogoUrl} alt="" className="w-6 h-6 rounded-full ring-2 ring-white/40 object-cover" aria-hidden="true" />
          ) : (
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-white/40',
                opportunity.clientAvatarColor
              )}
              aria-hidden="true"
            >
              {opportunity.clientName.charAt(0)}
            </div>
          )}
        </div>
      </div>

      {/* Bottom gradient overlay - days-ago hero, metric, caption */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8">
        {/* Row 1: calendar icon + days-ago (the key repurposing signal - emphasized) */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CALENDAR} size="xs" className="text-white" ariaHidden />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {opportunity.daysAgo}
            {t('repurpose_days_ago_short', { defaultValue: 'd ago' })}
          </span>
        </div>
        {/* Row 2: original metric */}
        <div className="flex items-center gap-1 mb-0.5">
          <i className="hm hm-graph text-secondary-300" style={{ fontSize: 10 }} aria-hidden="true" />
          <span className="text-[10px] text-white/80 drop-shadow-sm">
            {opportunity.originalMetric}
          </span>
        </div>
        {/* Row 3: caption (1-line clamp) */}
        <p className="text-[11px] text-white/90 line-clamp-1 drop-shadow-sm italic">
          {opportunity.caption.length > 40
            ? `${opportunity.caption.slice(0, 40)}…`
            : opportunity.caption}
        </p>
      </div>
    </div>
  );

  return (
    <HoverCard
      trigger={cardTrigger}
      side="right"
      align="start"
      className="w-[360px] lg:w-[380px]"
    >
      <RepurposePopover
        opportunity={opportunity}
        formatRelativeDate={formatRelativeDate}
        onStartRemix={handleStartRemix}
        onSeeOriginal={handleSeeOriginal}
        t={t}
      />
    </HoverCard>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="flex-none w-[160px] aspect-square bg-gray-200 rounded-xl animate-pulse" />
);

// ── Main Component ────────────────────────────────────────────────────────────

/** Map backend PortfolioRepurposeCandidate → frontend RepurposeOpportunity */
function mapCandidate(c: PortfolioRepurposeCandidate): RepurposeOpportunity {
  return {
    id: c.id,
    clientId: c.clientId,
    clientName: c.clientName,
    clientAvatarColor: c.clientAvatarColor,
    caption: c.caption,
    thumbnailPlaceholderGradient: c.thumbnailPlaceholderGradient,
    platforms:
      c.platforms && c.platforms.length > 0
        ? (c.platforms as RepurposeOpportunity['platforms'])
        : ['LINKEDIN'],
    channelNames: c.channelNames,
    channelProfilePhotoUrls: c.channelProfilePhotoUrls,
    lifecycleStage: c.lifecycleStage,
    publishedAt: c.publishedAt,
    daysAgo: c.daysAgo,
    originalMetric: c.originalMetric,
    originalMetricValue: c.originalMetricValue,
    reason: c.reason,
    totalImpressions: c.totalImpressions,
    totalReach: c.totalReach,
    totalEngagements: c.totalEngagements,
    media: c.media,
    clientLogoUrl: c.clientLogoUrl,
  };
}

export const RepurposingOpportunitiesStrip = () => {
  const { t } = useTranslation('agency');
  const { formatRelativeDate } = useTimezone();
  const { data } = usePortfolioHighlights();
  const opportunities: RepurposeOpportunity[] = (data?.repurposeCandidates ?? []).map(mapCandidate);

  // Collapsible state - persisted in localStorage; default collapsed
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STRIP_STORAGE_KEY);
      return stored !== null ? stored === 'true' : false;
    } catch {
      return false;
    }
  });

  const [toast, setToast] = useState<ToastState>({ visible: false, message: '' });
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    setToast({ visible: true, message });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ visible: false, message: '' });
    }, 2800);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const enterMutation = useMutation({
    mutationKey: ['enter-god-mode'],
    mutationFn: (teamId: number) => agencyService.enterGodMode(teamId),
  });

  const enterAndGo = useCallback(
    async (opportunity: RepurposeOpportunity, destination: string) => {
      const teamId = parseInt(opportunity.clientId, 10);
      if (!Number.isFinite(teamId)) return;
      try {
        const data = await enterMutation.mutateAsync(teamId);
        setAccessToken(data.accessToken);
        await refreshUser();
        navigate(destination);
      } catch {
        showToast(t('repurpose_enter_error', { defaultValue: 'Could not open brand.' }));
      }
    },
    [enterMutation, navigate, refreshUser, showToast, t],
  );

  const handleStartRemix = useCallback(
    (opportunity: RepurposeOpportunity) => {
      void enterAndGo(opportunity, `/creator?remix=${opportunity.id}`);
    },
    [enterAndGo],
  );

  const handleSeeOriginal = useCallback(
    (opportunity: RepurposeOpportunity) => {
      void enterAndGo(opportunity, `/creator?post=${opportunity.id}`);
    },
    [enterAndGo],
  );

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem(STRIP_STORAGE_KEY, String(next));
    } catch {
      // localStorage may be unavailable
    }
  };

  // Hide entirely when there are no repurposing candidates
  if (opportunities.length === 0) return null;

  return (
    <div className="relative z-10 bg-white border-b border-gray-900/10 portfolio-section">
      {/* ── Strip Header - editorial overline + GlossaryTooltip ─────────── */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
        {/* Overline title wrapped in GlossaryTooltip + collapsed thumbnail preview */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <GlossaryTooltip termId="metric-repurpose-opportunity" side="bottom" align="start">
            <h2 className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500 leading-tight cursor-default">
              {t('repurpose_strip_title', { defaultValue: 'Repurposing Opportunities' })}
            </h2>
          </GlossaryTooltip>
          {!isOpen && opportunities.length > 0 && (
            <button
              type="button"
              onClick={toggleOpen}
              className="flex items-center -space-x-1.5 isolate ml-1 rounded-full hover:opacity-80 transition-opacity border border-transparent focus:outline-none focus-visible:border-primary-600"
              aria-label={t('repurpose_expand_with_count', {
                defaultValue: 'Show {{count}} repurposing opportunities',
                count: opportunities.length,
              })}
            >
              {opportunities.slice(0, 5).map((p) => {
                const thumb = p.media?.[0]?.thumbnailUrl ?? p.media?.[0]?.originalUrl ?? null;
                return (
                  <div
                    key={p.id}
                    className="w-5 h-5 rounded-full ring-2 ring-white overflow-hidden flex-shrink-0"
                    style={thumb ? undefined : { background: p.thumbnailPlaceholderGradient }}
                    aria-hidden="true"
                  >
                    {thumb && (
                      <img
                        src={thumb}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                );
              })}
              {opportunities.length > 5 && (
                <div
                  className="w-5 h-5 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center text-[8px] font-semibold text-gray-600 flex-shrink-0"
                  aria-hidden="true"
                >
                  +{opportunities.length - 5}
                </div>
              )}
            </button>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggleOpen}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors shrink-0 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded px-1 py-0.5"
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? t('repurpose_collapse', { defaultValue: 'Collapse repurposing opportunities' })
              : t('repurpose_expand', { defaultValue: 'Show repurposing opportunities' })
          }
        >
          <SelectChevron className={`shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
          <span className="hidden sm:inline">
            {isOpen
              ? t('repurpose_collapse', { defaultValue: 'Collapse' })
              : t('repurpose_expand', { defaultValue: 'Show' })}
          </span>
        </button>
      </div>

      {/* ── Carousel ─────────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          className="px-4 sm:px-6 pb-4 pt-1"
          role="list"
          aria-label={t('repurpose_strip_title', { defaultValue: 'Repurposing Opportunities' })}
        >
          {opportunities.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 px-1">
              {t('repurpose_strip_empty', {
                defaultValue: 'No repurposing opportunities surfaced this week.',
              })}
            </p>
          ) : (
            <HorizontalCardCarousel>
              {opportunities.map((opportunity) => (
                <div key={opportunity.id} role="listitem">
                  <OpportunityCard
                    opportunity={opportunity}
                    onStartRemix={handleStartRemix}
                    onSeeOriginal={handleSeeOriginal}
                    formatRelativeDate={formatRelativeDate}
                    t={t}
                  />
                </div>
              ))}
            </HorizontalCardCarousel>
          )}
        </div>
      )}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toast.visible && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl shadow-xl"
        >
          <i
            className="hm hm-tick-circle text-primary-400"
            style={{ fontSize: 15 }}
            aria-hidden="true"
          />
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default RepurposingOpportunitiesStrip;
